/**
 * The one way this console prints a moment in time.
 *
 * Every instant the API answers with is an ISO string in UTC, precisely so the browser can draw
 * it in the reader's own zone — and an operator reading a roster needs the **time** as well as
 * the date: `last_seen_at` is throttled to one write per five minutes, so "today" is not an
 * answer to "when was this account last here". So one formatter, used everywhere a panel on this
 * tab prints an instant, rather than a `formatDate` per surface that can drift into two different
 * readings of the same column.
 *
 * `dateStyle: 'medium'` is what makes it unambiguous: a named month cannot be read as 3/4 in one
 * country and 4/3 in another, which a numeric locale date can and does.
 *
 * The exact ISO instant rides the element's `title`, the way the web build label carries its
 * commit: a formatted local time is the reading a person wants, and the precise value is one
 * hover away for somebody debugging.
 */

/** Local date and short time — `2 Jan 2026, 15:04` — in whatever the reader's locale says that is. */
const INSTANT_FORMAT: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' };

/** `null` is "this has never happened", which is a different fact from "we do not know when". */
export function formatInstant(iso: string | null): string {
  if (iso === null) return 'Never';
  return new Date(iso).toLocaleString(undefined, INSTANT_FORMAT);
}

/**
 * One instant, drawn. `title` is the raw ISO so the exact value is recoverable by hovering; a
 * `null` instant carries none, because there is no instant to be exact about.
 */
export function Instant({ iso, className }: { iso: string | null; className?: string }) {
  return (
    <span className={className} title={iso ?? undefined}>
      {formatInstant(iso)}
    </span>
  );
}
